import { useMemo, useState } from 'react'
import { AlertTriangle, Building2, CheckCircle, Clock, List, TrendingUp, Users } from 'lucide-react'
import AppLayout from '../AppLayout'
import BSDCard from '../BSDCard'
import AutoGrid from '../AutoGrid'
import DistrictMap from '../DistrictMap'
import { CDI_LIST, type Cdi } from '../../data/cdi'
import { CdiDialog } from '../StrutturaDetailDialog'
import CdiDialogList from './CdiDialogList'
import type { AmbitoId } from '../../data/ambitiStats'

export default function CdiPage() {
  return (
    <AppLayout>
      {({ selectedAmbito, onSelectedAmbitoChange }) => (
        <CdiContent
          selectedAmbito={selectedAmbito}
          onSelectedAmbitoChange={onSelectedAmbitoChange}
        />
      )}
    </AppLayout>
  )
}

function CdiContent({
  selectedAmbito,
  onSelectedAmbitoChange,
}: {
  selectedAmbito: AmbitoId | null
  onSelectedAmbitoChange: (id: AmbitoId | null) => void
}) {
  const filtered = useMemo(
    () => selectedAmbito ? CDI_LIST.filter(c => c.ambitoId === selectedAmbito) : CDI_LIST,
    [selectedAmbito],
  )

  const [selectedCdi, setSelectedCdi] = useState<Cdi | null>(null)

  const stats = useMemo(() => {
    const totPostiAccreditati = filtered.reduce((s, c) => s + (c.postiAccreditati ?? 0), 0)
    const totListaFT = filtered.reduce((s, c) => s + (c.listaAttesaFullTime ?? 0), 0)
    const totListaPT = filtered.reduce((s, c) => s + (c.listaAttesaPartTime ?? 0), 0)
    const totListaTotale = totListaFT + totListaPT
    const conListaAperta = filtered.filter(c => (c.listaAttesaFullTime ?? 0) > 0 || (c.listaAttesaPartTime ?? 0) > 0)
    const senzaLista = filtered.filter(c => (c.listaAttesaFullTime ?? 0) === 0 && (c.listaAttesaPartTime ?? 0) === 0)
    const byListaFT = [...filtered].filter(c => c.listaAttesaFullTime > 0).sort((a, b) => b.listaAttesaFullTime - a.listaAttesaFullTime)
    const byListaPT = [...filtered].filter(c => c.listaAttesaPartTime > 0).sort((a, b) => b.listaAttesaPartTime - a.listaAttesaPartTime)
    const rapportoListaPosti = totPostiAccreditati > 0 ? Math.round((totListaTotale / totPostiAccreditati) * 100) : null
    return { totPostiAccreditati, totListaFT, totListaPT, totListaTotale, conListaAperta, senzaLista, byListaFT, byListaPT, rapportoListaPosti }
  }, [filtered])

  return (
    <section className="telemetry-dashboard">
      <div className="telemetry-inner">
        <h3 className="telemetry-section-title" style={{ marginBottom: 0 }}>
          {selectedAmbito
            ? `Ambito di ${selectedAmbito.charAt(0).toUpperCase() + selectedAmbito.slice(1)} - Centri Diurni Integrati`
            : 'Provincia di Monza e Brianza - Centri Diurni Integrati'}
        </h3>

        {/* ── Prima riga ── */}
        <AutoGrid>

          {/* Card 1 — Cos'è un CDI */}
          <BSDCard kind="panel" sezione="Centri Diurni Integrati" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            <p style={{ color: 'var(--text-1)', fontSize: 12, lineHeight: 1.6, margin: 0 }}>
              I <strong>Centri Diurni Integrati (CDI)</strong> sono strutture
              socio-sanitarie semi-residenziali rivolte ad anziani parzialmente o totalmente non autosufficienti,
              che offrono assistenza diurna senza ricovero notturno.
              Svolgono anche una funzione di <strong>sostegno ai caregiver</strong>,
              alleggerendo il carico delle famiglie e ritardando o evitando il ricovero in RSA.
            </p>
          </BSDCard>

          {/* Card 2 — Mappa ambiti */}
          <BSDCard
            className="telemetry-map-card"
            kind="kpi"
            sezione="Centri Diurni Integrati"
            style={{ minHeight: 0, padding: 10, overflow: 'hidden' }}
          >
            <DistrictMap
              onSelectDistrict={(id) => onSelectedAmbitoChange(id as AmbitoId)}
              onClearSelection={() => onSelectedAmbitoChange(null)}
              selectedDistrictId={selectedAmbito}
            />
          </BSDCard>

          {/* Card 3 — Elenco */}
          <BSDCard
            kind="kpi"
            sezione="Centri Diurni Integrati"
            label="Elenco strutture"
            value={String(filtered.length)}
            icon={Building2}
            color="#10b981"
            zoomable
            hideZoomButton
            sub={
              <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
                Tutti i Centri Diurni Integrati nel territorio selezionato.
              </p>
            }
            zoomSub={<CdiDialogList cdi={filtered} />}
            footer={(openZoom) => (
              <button
                type="button"
                onClick={openZoom}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  width: '100%',
                  padding: '6px 0',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--accent)',
                  background: 'none',
                  border: 'none',
                  borderTop: '1px solid var(--border)',
                  cursor: 'pointer',
                  justifyContent: 'center',
                  marginTop: 8,
                  paddingTop: 8,
                }}
              >
                <List size={14} />
                Vedi elenco completo
              </button>
            )}
          />

          {/* Card 4 — Pressione lista */}
          <BSDCard kind="panel" sezione="Centri Diurni Integrati" style={{ display: 'flex', flexDirection: 'column' }}>
            <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <TrendingUp size={14} style={{ color: '#10b981' }} />
              Pressione lista
            </h3>
            {stats.rapportoListaPosti !== null ? (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: 32, fontWeight: 800, color: '#10b981', lineHeight: 1 }}>
                    {stats.rapportoListaPosti}%
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 4 }}>lista/posti</span>
                </div>
                <div style={{ height: 8, borderRadius: 4, background: 'var(--bg-2)', overflow: 'hidden', marginBottom: 10 }}>
                  <div style={{ height: '100%', width: `${Math.min(stats.rapportoListaPosti, 100)}%`, background: '#10b981', borderRadius: 4, transition: 'width 0.4s' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-3)' }}>
                  <span>{stats.totListaTotale} in lista</span>
                  <span>{stats.totPostiAccreditati} posti</span>
                </div>
              </>
            ) : (
              <p style={{ fontSize: 12, color: 'var(--text-3)', margin: 0 }}>Dati non disponibili.</p>
            )}
          </BSDCard>

          {/* Card 5 — Persone in lista d'attesa */}
          <BSDCard
            kind="kpi"
            sezione="Centri Diurni Integrati"
            label="Persone in lista d'attesa"
            value={stats.totListaTotale.toLocaleString('it-IT')}
            icon={Clock}
            color="#f59e0b"
            sub={
              <div style={{ display: 'grid', gap: 4, marginTop: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-3)' }}>
                  <span>Full-time</span>
                  <span style={{ fontWeight: 700, color: '#f59e0b' }}>{stats.totListaFT}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-3)' }}>
                  <span>Part-time</span>
                  <span style={{ fontWeight: 700, color: '#8b5cf6' }}>{stats.totListaPT}</span>
                </div>
              </div>
            }
          />

        </AutoGrid>

        {/* ── Seconda riga ── */}
        <AutoGrid style={{ marginTop: 14 }}>

          {/* Card 6 — Posti accreditati */}
          <BSDCard
            kind="kpi"
            sezione="Centri Diurni Integrati"
            label="Posti accreditati"
            value={stats.totPostiAccreditati.toLocaleString('it-IT')}
            icon={Users}
            color="#0ea5e9"
            sub={
              <div style={{ display: 'grid', gap: 4 }}>
                {[...filtered].filter(c => c.postiAccreditati > 0).sort((a, b) => b.postiAccreditati - a.postiAccreditati).map(c => (
                  <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                    <span
                      onClick={() => setSelectedCdi(c)}
                      style={{ fontSize: 10, color: 'var(--text-2)', cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >{c.nome}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#0ea5e9', flexShrink: 0 }}>{c.postiAccreditati}</span>
                  </div>
                ))}
              </div>
            }
          />

          {/* Card 7 — Lista attesa Full-time */}
          <BSDCard
            kind="kpi"
            sezione="Centri Diurni Integrati"
            label="In lista d'attesa Full-time"
            value={stats.totListaFT.toLocaleString('it-IT')}
            icon={Clock}
            color="#f59e0b"
            sub={
              stats.byListaFT.length > 0 ? (
                <div style={{ display: 'grid', gap: 4 }}>
                  {stats.byListaFT.map(c => (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                      <span
                        onClick={() => setSelectedCdi(c)}
                        style={{ fontSize: 10, color: 'var(--text-2)', cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >{c.nome}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', flexShrink: 0 }}>{c.listaAttesaFullTime}</span>
                    </div>
                  ))}
                </div>
              ) : <p style={{ fontSize: 10, color: 'var(--text-3)', margin: 0 }}>Nessuna lista aperta.</p>
            }
          />

          {/* Card 8 — Lista attesa Part-time */}
          <BSDCard
            kind="kpi"
            sezione="Centri Diurni Integrati"
            label="In lista d'attesa Part-time"
            value={stats.totListaPT.toLocaleString('it-IT')}
            icon={Clock}
            color="#8b5cf6"
            sub={
              stats.byListaPT.length > 0 ? (
                <div style={{ display: 'grid', gap: 4 }}>
                  {stats.byListaPT.map(c => (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                      <span
                        onClick={() => setSelectedCdi(c)}
                        style={{ fontSize: 10, color: 'var(--text-2)', cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >{c.nome}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#8b5cf6', flexShrink: 0 }}>{c.listaAttesaPartTime}</span>
                    </div>
                  ))}
                </div>
              ) : <p style={{ fontSize: 10, color: 'var(--text-3)', margin: 0 }}>Nessuna lista aperta.</p>
            }
          />

          {/* Card 9 — CDI con lista aperta */}
          <BSDCard
            kind="kpi"
            sezione="Centri Diurni Integrati"
            label="CDI con lista aperta"
            value={`${stats.conListaAperta.length} / ${filtered.length}`}
            icon={AlertTriangle}
            color="#ef4444"
            sub={
              <div style={{ display: 'grid', gap: 4 }}>
                {stats.conListaAperta.map(c => (
                  <div
                    key={c.id}
                    onClick={() => setSelectedCdi(c)}
                    style={{ fontSize: 10, color: 'var(--text-2)', borderLeft: '2px solid #ef4444', paddingLeft: 6, cursor: 'pointer', lineHeight: 1.4 }}
                  >
                    {c.nome}
                  </div>
                ))}
              </div>
            }
          />

        </AutoGrid>

        <CdiDialog cdi={selectedCdi} onClose={() => setSelectedCdi(null)} fromUrl="/cdi" />
      </div>
    </section>
  )
}
